<template>
  <div class="flights-landing-page">
    <section class="flights-search" :style="content.headerImg ? 'background-image: url(' + content.headerImg + ')' : ''">
      <div class="container">
        <div class="row">
          <div class="multi-product-search">
            <ul class="product-tabs">
              <li
                v-for="(product, productIndex) in productTabs"
                :key="productIndex"
                @click="currentProduct = product.ref"
                :class="{ 'active' : currentProduct === product.ref }"
                >
                <i :class="product.icon"></i>
                <span class="label">{{product.name}}</span>
              </li>
            </ul>
            <div class="product-search-widget">
              <FlightSearch v-if="currentProduct === 'flights'" :showSearchWidget="true" :showSearchToggle="false" />
              <HotelSearch v-if="currentProduct === 'hotels'" :showSearchWidget="true" :showSearchToggle="false" />
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <section class="container">
      <div class="row">
        <div class="page-body">

          <div class="search-history" v-if="getSearchHistory.length">
            <h2>{{$t('recentFlightSearches')}}</h2>
            <ul>
              <li v-for="(search, index) in searchHistoryLimited" :key="index">
                <div class="history-item">
                  <i class="fal fa-plane-alt"></i>

                  <div class="search-details">
                    <p>
                        {{ $t('departureToDestination', { originName: search.origin.name, destinationName: search.destination.name }) }}
                    </p>
                    <p v-if="search.oneWayDate">
                      {{ search.oneWayDate | moment("ddd, MMM Do") }}
                    </p>
                    <p v-if="search.roundTripDates">
                      {{ search.roundTripDates.start | moment("ddd, MMM Do") }}
                      to {{ search.roundTripDates.end | moment("ddd, MMM Do") }}
                    </p>
                  </div>

                  <button class="btn btn-sm" title="Search" @click="executeSearch(search)">Search</button>
                </div>
              </li>
            </ul>
          </div>

          <div class="top-destinations-widget">
            <h2>{{ $t('exploreNewDestinations') }}</h2>
            <div class="destination-cards" v-if="!loadingDestinations">
              <div 
                class="destination-card" 
                v-for="(destination, destinationIndex) in topDestinations" 
                :key="destinationIndex"
                @click="$router.push(destination.link)"
                >
                <div class="name" :style="'background-image: url(' + destination.thumbImg + ')'">
                  <h4>{{ destination.data.label }}</h4>
                </div>
                <div class="info">
                  <h5>{{ destination.snippet }}</h5>

                  <div class="price-cta">
                    <div class="price">
                      <p class="label">{{ $t('from') }}</p>
                      <p class="rate">{{ destination.lowRate | formatCurrency(getCurrencyCode, 0) }}</p>
                    </div>

                    <button class="btn btn-sec">{{ $t('bookNow') }}</button>
                  </div>
                </div>
              </div>
            </div>

            <div class="destination-cards" v-if="loadingDestinations">
              <div class="destination-card loading-filters" v-for="(loadingItem, loadingItemIndex) in destinationList.length" :key="loadingItemIndex">
                <div class="name">
                  <i class="fal fa-image"></i>
                </div>
                <div class="info">
                  <h5><div class="short"></div></h5>
                </div>
              </div>
            </div>
          </div>
          
          <div class="flight-offers-widget">
            <h2>{{ $t('latestFlightDeals') }}</h2>

            <div class="flight-cards" v-if="!loadingDestinations">
              <div class="flight-card" v-for="(offer, offerIndex) in topFlights" :key="offerIndex">
                <div class="card-body" @click="$router.push(offer.link)">
                  <div class="route-dates">
                    <p class="route">{{ offer.from.value | toCityName }} to {{ offer.to.value | toCityName }}</p>
                  </div>
                  <div class="price" v-if="offer.lowRate">
                    <span class="label">{{ $t('from') }}</span>
                    <span class="rate">{{ offer.lowRate | formatCurrency(getCurrencyCode, 0) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="flight-cards loading-filters" v-if="loadingDestinations">
              <div class="flight-card" v-for="(offer, loadingOfferIndex) in flightOffers.length" :key="loadingOfferIndex">
                <div class="card-body">
                  <i class="fal fa-spinner-third fa-spin"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import AgencyApi from "@/services/agencyApi";
import HotelsApi from "@/services/hotelsApi";
import FlightsApi from "@/services/flightsApi";


export default {
  name: 'Home',
  data () {
    return {
      title: 'Search & Book Discount Travel',
      content: {},
      moment: this.$moment,
      currentProduct: 'flights',
      hotelsApi: null,
      flightsApi: null,
      agencyApi: null,
      destination: {},
      loadingDestinations: null,
      loadingFlightOffers: null,
      productTabs: [
        {
          name: this.$t('flights'),
          ref: 'flights',
          icon: 'fal fa-plane'
        },
        {
          name: this.$t('hotels'),
          ref: 'hotels',
          icon: 'fal fa-hotel'
        }
      ],
      destinationList: [],
      topDestinations: [],
      flightOffers: [],
      topFlights: []
    }
  },

  metaInfo: {
      title: 'Search & Book Discount Travel',
  },

  components: {
    'FlightSearch': () => import (/* webpackChunkName: "flightSearchWidget" */ '@/components/flights/FlightSearchWidget'),
    'HotelSearch': () => import (/* webpackChunkName: "hotelSearchWidget" */ '@/components/hotels/HotelSearchWidget'),
  },

  computed: {
    ...mapGetters ([
      'getPageContent',
      'getSearchHistory',
      'getCurrencyCode',
    ]),

    searchHistoryLimited() {
        return this.getSearchHistory.slice(0, 3)
    }

  },

  created() {
    let self = this;
    self.agencyApi = new AgencyApi();
    self.hotelsApi = new HotelsApi();
    self.flightsApi = new FlightsApi();
  },

  async mounted() {
    let self = this;
    self.content = await self.getPageContent('bookinghome');
    self.destinationList = self.content.destinations;
    self.flightOffers = self.content.flightOffers;
    self.loadingDestinations = true;
    await self.setupTopDestinations();
    await self.setupFlightOffers();
    self.loadingDestinations = false;
  },

  methods: {

    getDestination: async function(destination) {
      let self = this;
      let destinationContent = await self.agencyApi.getDestination(destination);
      return destinationContent.data;
    },

    setupTopDestinations: function() {
      let self = this;
      
      for(let i = 0; i < self.destinationList.length; i++) {
        self.setupDestination(self.destinationList[i]);
      }
    },

    setupDestination: async function(destination) {
      let self = this;
      let dc = await self.getDestination(destination);
      let locale = self.$i18n.locale;
      let startDate = self.$moment().add(14, 'days').toDate();
      startDate = self.$moment(startDate).format("YYYY-MM-DD");
      let endDate = self.$moment().add(21, 'days').toDate();
      endDate = self.$moment(endDate).format("YYYY-MM-DD");
      if(dc.data) {
        dc.link = `/${locale}/hotels/search/${self.getCurrencyCode}/${dc.data.type}-${dc.data.label.replace(/\s/g , "-")}-${dc.data.state}-${dc.data.country}/${dc.data.value}/${startDate}/${endDate}?rooms=1&adults=2&children=0&sort=gs`;          
      }

      let lowRate = await self.hotelsApi.cheapestOne(
          window.TravelPlatform.key,
          (dc.data.type === 'city' ? dc.data.value : null),
          (dc.data.type === 'hotel' ? dc.data.value : null),
          (dc.data.type === 'airport' ? dc.data.value : null),
          startDate,
          endDate,
          1, 2, 0,
          self.getCurrencyCode,
          'lp', 1);

      if(lowRate.data) {
        dc.lowRate = lowRate.data['getHotelExpress.Results'].results.hotel_data[0].room_data[0].rate_data[0].price_details.display_total;
      }

      self.topDestinations.push(dc);
    },

    setupFlightOffers: async function () {
      let self = this;
      self.loadingFlightOffers = true;
      self.flightOffers.forEach((offer) => self.setupFlightOffer(offer));
      self.loadingFlightOffers = false;
    },

    setupFlightOffer: async function(offer) {
      let self = this; 
      
      let locale = self.$i18n.locale;
      let fromDate = self.$moment().add(14, 'days').toDate();
      fromDate = self.$moment(fromDate).format("YYYY-MM-DD");
      offer.fromDate = fromDate;
      
      let returnDate;

      if(offer.roundTrip) {
        returnDate = self.$moment().add(21, 'days').toDate();
        returnDate = self.$moment(returnDate).format("YYYY-MM-DD");
        offer.returnDate = returnDate
      }

      offer.link = `/${locale}/flights/search/${self.getCurrencyCode}/${offer.from.value}/${offer.to.value}/${fromDate}/${offer.returnDate ? returnDate : ''}?adults=1&children=0&infants=0&seatClass=economy&sortOrder=cheapest`;          
      let lowRate = await self.flightsApi.cheapestOne(window.TravelPlatform.key, self.getCurrencyCode, offer.from.value, offer.to.value, fromDate, returnDate);
      offer.lowRate = lowRate.data.offers.offerList[0].price.total;

      self.topFlights.push(offer);
    },
  },

  watch: {
    'getCurrencyCode': async function() {
      let self = this;
      self.topDestinations = [];
      self.topFlights = [];
      await self.setupTopDestinations();
      await self.setupFlightOffers();
    },
  }
  
}
</script>
